/** OKLCH and WCAG helpers for theme derivation, after Björn Ottosson's OKLab. */

export type Rgb = { r: number; g: number; b: number };
export type Oklch = { l: number; c: number; h: number };

const GAMUT_EPSILON = 1e-4;
const CHROMA_STEP = 0.005;

export const parseHex = (hex: string): Rgb => {
  const value = hex.replace("#", "");
  return {
    r: parseInt(value.slice(0, 2), 16),
    g: parseInt(value.slice(2, 4), 16),
    b: parseInt(value.slice(4, 6), 16),
  };
};

export const toHex = ({ r, g, b }: Rgb): string =>
  "#" +
  [r, g, b]
    .map((v) => v.toString(16).padStart(2, "0"))
    .join("")
    .toUpperCase();

export const toChannels = ({ r, g, b }: Rgb): string => `${r} ${g} ${b}`;

const toLinear = (v: number) => {
  const s = v / 255;
  return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
};

const fromLinear = (v: number) =>
  v <= 0.0031308 ? 12.92 * v : 1.055 * v ** (1 / 2.4) - 0.055;

export const rgbToOklch = ({ r, g, b }: Rgb): Oklch => {
  const [lr, lg, lb] = [toLinear(r), toLinear(g), toLinear(b)];
  const l = Math.cbrt(
    0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb,
  );
  const m = Math.cbrt(
    0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb,
  );
  const s = Math.cbrt(
    0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb,
  );
  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const a = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const bb = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  const h = (Math.atan2(bb, a) * 180) / Math.PI;
  return { l: L, c: Math.hypot(a, bb), h: h < 0 ? h + 360 : h };
};

const oklchToLinear = ({ l, c, h }: Oklch): [number, number, number] => {
  const rad = (h * Math.PI) / 180;
  const a = c * Math.cos(rad);
  const b = c * Math.sin(rad);
  const l_ = (l + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m_ = (l - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s_ = (l - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return [
    4.0767416621 * l_ - 3.3077115913 * m_ + 0.2309699292 * s_,
    -1.2684380046 * l_ + 2.6097574011 * m_ - 0.3413193965 * s_,
    -0.0041960863 * l_ - 0.7034186147 * m_ + 1.707614701 * s_,
  ];
};

const inGamut = (linear: number[]) =>
  linear.every((v) => v >= -GAMUT_EPSILON && v <= 1 + GAMUT_EPSILON);

/** Lowers chroma in fixed steps until the colour fits sRGB, keeping L and H. */
export const oklchToRgb = (color: Oklch): Rgb => {
  const l = Math.min(1, Math.max(0, color.l));
  let c = Math.max(0, color.c);
  let linear = oklchToLinear({ l, c, h: color.h });
  while (!inGamut(linear) && c > 0) {
    c = Math.max(0, c - CHROMA_STEP);
    linear = oklchToLinear({ l, c, h: color.h });
  }
  const [r, g, b] = linear.map((v) =>
    Math.round(
      Math.min(1, Math.max(0, fromLinear(Math.min(1, Math.max(0, v))))) * 255,
    ),
  );
  return { r, g, b };
};

export const relativeLuminance = ({ r, g, b }: Rgb): number =>
  0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);

export const contrastRatio = (x: Rgb, y: Rgb): number => {
  const [hi, lo] = [relativeLuminance(x), relativeLuminance(y)].sort(
    (p, q) => q - p,
  );
  return (hi + 0.05) / (lo + 0.05);
};
